import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import FieldFormFields from 'components/forms/FieldFormFields';

import useField from 'hooks/useField';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props, UpdateFieldFormSchema } from './types';

import styles from './styles.module.scss';

const UpdateFieldForm: FunctionComponent<Props> = ({
    className = '',
    formValues,
    isClosing,
    onClose,
    setIsClosing,
    testId = TEST_ID
}) => {
    const { updateField } = useField();
    const { SAVE } = useTranslation();

    const defaultValues: UpdateFieldFormSchema = {
        client: {
            label: formValues?.clientName ?? '',
            value: formValues?.clientId ?? ''
        },
        farm: {
            label: formValues?.parcelName ?? '',
            value: formValues?.parcelId ?? ''
        },
        id: formValues?.id ?? '',
        name: formValues?.name ?? ''
    };

    const form = useForm<UpdateFieldFormSchema>({
        defaultValues,
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose: () => setIsClosing?.(false),
        onClose,
        requestingClose: isClosing
    });

    const onSubmit = async (data: UpdateFieldFormSchema) => {
        if (!formValues) {
            return;
        }

        const field = await updateField({
            field: {
                name: data.name,
                parcelId: data.farm.value
            },
            id: formValues.id
        });

        if (field) {
            onClose?.();
        }
    };

    return (
        <VerticalContainer className={`${styles.updateFieldForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <FieldFormFields />

                    <FormSubmitPanel
                        buttonText={SAVE}
                        className={styles.formSubmitPanel}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default UpdateFieldForm;
